<template>
  <div class="">
    <div
      v-if="loading"
    >
      <i class="fa fa-circle-o-notch fa-spin fa-fw text-muted"></i>
    </div>
    <div
      v-if="!loading && !people.length"
    >
      <div
        v-if="isStaff"
      >
        <p>
          As an employee of Redwood, you don't have any accounting
          profiles. If you feel you have reached this message in error,
          please <a href="#" @click.prevent="showHelpModal()">ask for help</a>.
        </p>
      </div>
      <div
        v-else
      >
        <b>Oops!</b> It looks like someone forgot to finish setting
        up your accounting profiles for you in accessRedwood. Don't
        worry—nothing is wrong with your accounts, they just haven't
        been connected to accessRedwood yet. We've already let
        someone know.
      </div>
    </div>
    <div
      v-if="!loading && people.length"
    >
      <p
        v-if="needsAction"
        class="mb-4"
      >
        Thank you for reviewing the contact information we have
        on file for you in our accounting department. We'll be
        reviewing and taking action on any requests you've made ASAP.
        Requests are handled in the order they are received.
      </p>
      <p
        v-else-if="needsAudit"
        class="mb-4"
      >
        The information below is what we have on file for you
        in our accounting department. Please review this information
        carefully. If there are errors, please click <b>This is wrong</b>
        to report the problem, and we will correct it. Requests
        are handled in the order they are received.
      </p>
      <p
        v-else
        class="mb-4"
      >
        Thank you for reviewing the contact information we have
        on file for you in our accounting department. You're all set.
      </p>
      <div
        v-for="person in people"
        :key="person.personID"
      >
        <div
          v-for="address in person.addresses"
          :key="address.addressId"
          class="card mb-3"
        >
          <div class="card-body">
            <address
              class="mb-0 position-relative"
            >
              <div>{{ person.personName }}</div>
              <div
                v-if="person.printedName && person.printedName !== person.personName"
              >
                {{ person.printedName }}
              </div>
              {{ address.address }}
              <div
                class="small mt-2 text-muted mb-2"
              >
                Last updated
                {{ ago(address.modifyDate) }}
              </div>
              <div>
                <b-input-group>
                  <template #prepend>
                    <b-input-group-text>
                      <small>Account #</small>
                    </b-input-group-text>
                  </template>
                  <b-input
                    readonly
                    :value="person.personId"
                  ></b-input>
                  <template #append>
                    <share-button
                      btn-class="btn btn-white"
                      icon="clipboard"
                      :url="person.personId"
                    >
                      Copy
                    </share-button>
                  </template>
                </b-input-group>
              </div>
              <hr>
              <div
                v-if="address.needs_audit"
              >
                <button
                  class="btn btn-sm btn-primary mr-1"
                  @click.prevent="sendGoodAudit(address)"
                >
                  <i class="fa fa-thumbs-up mr-1"></i>
                  This is correct
                </button>
                <button
                  class="btn btn-sm btn-white"
                  @click.prevent="showAudit(person, address)"
                >
                  <i class="fa fa-thumbs-down mr-1"></i>
                  This is wrong
                </button>
              </div>
              <div
                v-if="!address.needs_audit"
              >
                <div
                  class="media align-items-center"
                >
                  <div
                    v-if="address.audit_status === 'bad'"
                    class="media-body small"
                  >
                    <i class="fa fa-thumbs-down mr-1"></i>
                    You let us know this was wrong, and we'll fix it.
                  </div>
                  <div
                    v-if="address.audit_status === 'good'"
                    class="media-body small"
                  >
                    <i class="fa fa-thumbs-up mr-1"></i>
                    You let us know this was correct.
                  </div>
                  <div class="media-right">
                    <button
                      class="btn btn-sm btn-white ml-2"
                      @click.prevent="showAudit(person, address)"
                    >
                      Report a problem
                    </button>
                  </div>
                </div>
              </div>
              <span
                :class="[
                  'badge badge-address-type text-uppercase',
                  {
                    'badge-dark': !address.needs_audit,
                    'badge-danger': address.needs_audit
                  }
                ]"
              >
                {{ address.addressType }}
              </span>
            </address>
          </div>
        </div>
      </div>
    </div>
    <b-modal
      :visible="showAuditModal"
      :hide-header="true"
      :hide-footer="true"
      no-close-on-backdrop
    >
      <div
        v-if="error"
        class="alert alert-danger"
      >
        {{ error }}
      </div>
      <p>
        Please make any corrections below and include
        any additional information you need for us to know
        about these changes. Then click <b>Send</b>.
      </p>
      <textarea
        v-model="audit"
        class="form-control"
        rows="8"
      ></textarea>
      <hr>
      <div class="">
        <button
          class="btn btn-primary"
          :disabled="sendingAudit"
          @click.prevent="sendBadAudit()"
        >
          <i class="fa fa-paper-plane mr-1"></i>
          {{ sendingAudit ? 'Sending...' : 'Send' }}
        </button>
        <button
          class="btn btn-link"
          :disabled="sendingAudit"
          @click.prevent="showAuditModal = false"
        >
          Cancel
        </button>
      </div>
    </b-modal>
  </div>
</template>

<style lang="scss" scoped>
.badge-address-type {
  position: absolute;
  top: -5px;
  right: -5px;
}
</style>

<script>
import $ from 'jquery'
import { getUserAccounts } from '@/api'
import moment from 'moment'
import { MessageBag } from '@/lib/MessageBag'
import { auditAddress } from '@/api'
import { filter } from 'lodash'
import ShareButton from '@/components/ShareButton'

const $window = $(window)

export default {
  components: {
    ShareButton
  },
  props: {
    userId: {
      type: [String, Number],
      required: true
    },
    isStaff: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  data () {
    return {
      loading: true,
      people: [],
      auditAddress: null,
      audit: null,
      showAuditModal: false,
      sendingAudit: false,
      error: null
    }
  },
  computed: {
    needsAction () {
      if (this.people.length) {
        return filter(this.people, (person) => {
          return filter(person.addresses, (address) => {
            return !address.needs_audit && address.audit_status === 'bad'
          }).length
        }).length
      }
      return false
    },
    needsAudit () {
      if (this.people.length) {
        return filter(this.people, (person) => {
          return filter(person.addresses, (address) => {
            return address.needs_audit
          }).length
        }).length
      }
      return false
    }
  },
  mounted () {
    this.loadAccounts()
  },
  methods: {
    async loadAccounts () {
      let response = await getUserAccounts(this.userId)
      this.people = response.data
      this.loading = false
    },
    showHelpModal () {
      $window.trigger('help.requested')
    },
    ago (date) {
      return moment(date).fromNow()
    },
    showAudit (person, address) {
      this.auditAddress = address
      this.audit = person.personName + "\n" + address.address
      this.error = null
      this.showAuditModal = true
    },
    async sendBadAudit () {
      this.sendingAudit = true
      try {
        let response = await auditAddress(this.userId, this.auditAddress.addressId, 'bad', this.audit)
        this.auditAddress.needs_audit = false
        this.auditAddress.audit_status = 'bad'
        this.showAuditModal = false
      } catch (e) {
        console.log(e)
        // this.error = MessageBag.fromResponse(e.response).first()
      } finally {
        this.sendingAudit = false
      }
    },
    async sendGoodAudit(address) {
      this.sendingAudit = true
      try {
        let response = await auditAddress(this.userId, address.addressId, 'good')
        address.needs_audit = false
        address.audit_status = 'good'
        this.showAuditModal = false
      } catch (e) {
        this.error = MessageBag.fromResponse(e.response).first()
      } finally {
        this.sendingAudit = false
      }
    }
  }
}
</script>
